import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@mui/material';
import { SidePanel } from '../../panel/SidePanel';
import { FormField } from '../../panel/FormField';
import { SmartSelect, DateField, type SmartSelectOption } from '../../dropdown';
import type { CustomerServiceCreateRequest } from '@techgitter/contracts';
import { getServiceCatalog } from '../../../services/customerServicesApi';
import {
  CUSTOMER_SERVICE_STATUS_OPTIONS,
  SERVICE_STATUS_ACTIVE,
  SERVICE_STATUS_ONBOARDING,
} from './customerServiceStatusOptions';

interface ServiceCatalogItem {
  serviceId: string;
  serviceKey: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
}

interface AddCustomerServiceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CustomerServiceCreateRequest) => Promise<void>;
  existingServiceIds: string[];
  /** Named in the Gorelo confirmation ("…create a new client for <customer>…"). */
  customerName?: string | null;
}

/** Service catalog key whose add creates a client in Gorelo (sm4rt-api calls
 *  sm4rt-gorelo-integration before writing the row), so it is confirmed first. */
export const GORELO_SERVICE_KEY = 'gorelo';

export function requiresGoreloConfirmation(serviceKey: string | null | undefined): boolean {
  return (serviceKey ?? '').trim().toLowerCase() === GORELO_SERVICE_KEY;
}

export function goreloConfirmationMessage(customerName: string | null | undefined): string {
  const name = customerName?.trim() || 'this customer';
  return `Enabling Gorelo will create a new client for ${name} in Gorelo - proceed?`;
}

const ACTIVE_STATUS_VALUE = SERVICE_STATUS_ACTIVE;
const ONBOARDING_STATUS_VALUE = SERVICE_STATUS_ONBOARDING;

function getTodayDateString(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
}

export const AddCustomerServiceModal: React.FC<AddCustomerServiceModalProps> = ({
  open,
  onClose,
  onSubmit,
  existingServiceIds,
  customerName,
}) => {
  const today = getTodayDateString();
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [status, setStatus] = useState<number>(ACTIVE_STATUS_VALUE);
  const [onboardingDate, setOnboardingDate] = useState(today);
  const [startDate, setStartDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingGorelo, setConfirmingGorelo] = useState(false);
  const startDateManuallySet = useRef(false);

  useEffect(() => {
    if (open) {
      loadServiceCatalog();
    }
  }, [open]);

  const loadServiceCatalog = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getServiceCatalog({ activeOnly: true });
      setServices(result.items || []);
    } catch (err) {
      setError('Failed to load services');
      console.error('Error loading service catalog:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedServiceId) {
      setError('Please select a service');
      return;
    }

    // Adding Gorelo creates a client in Gorelo — make the user say so first.
    const selectedService = services.find((s) => s.serviceId === selectedServiceId);
    if (requiresGoreloConfirmation(selectedService?.serviceKey) && !confirmingGorelo) {
      setError(null);
      setConfirmingGorelo(true);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload: CustomerServiceCreateRequest = {
        serviceId: selectedServiceId,
        status,
        onboardingDate: onboardingDate || null,
        startDate: status === ONBOARDING_STATUS_VALUE ? null : (startDate || null),
        noteText: notes.trim() ? notes.trim() : null,
      };
      await onSubmit(payload);
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add service';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedServiceId('');
    setStatus(ACTIVE_STATUS_VALUE);
    setOnboardingDate(today);
    setStartDate(today);
    setNotes('');
    setError(null);
    setConfirmingGorelo(false);
    startDateManuallySet.current = false;
    onClose();
  };

  const handleStatusChange = (nextStatus: number) => {
    setStatus(nextStatus);
    startDateManuallySet.current = false;
    const nextToday = getTodayDateString();

    if (nextStatus === ACTIVE_STATUS_VALUE) {
      setOnboardingDate(nextToday);
      setStartDate(nextToday);
      return;
    }

    if (nextStatus === ONBOARDING_STATUS_VALUE) {
      setOnboardingDate(nextToday);
      setStartDate('');
    }
  };

  const availableServices = services.filter(
    (service) => !existingServiceIds.includes(service.serviceId)
  );

  const serviceOptions: SmartSelectOption[] = availableServices.map((service) => ({
    value: service.serviceId,
    label: service.displayName,
  }));

  const statusOptions: SmartSelectOption[] = CUSTOMER_SERVICE_STATUS_OPTIONS.filter(
    (option) => option.value === ACTIVE_STATUS_VALUE || option.value === ONBOARDING_STATUS_VALUE
  ).map((option) => ({ value: String(option.value), label: option.label }));

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      aria-label="Add service"
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          overflow: 'visible',
        },
      }}
    >
      {confirmingGorelo ? (
        <SidePanel
          title="Enable Gorelo"
          submitLabel={isSaving ? 'Creating client…' : 'Proceed'}
          isValid={!isSaving}
          onClose={() => {
            // Cancel / × steps back to the form rather than discarding it.
            if (!isSaving) {
              setConfirmingGorelo(false);
              setError(null);
            }
          }}
          onSubmit={handleSubmit}
        >
          <div className="side-panel__alert side-panel__alert--warning">
            {goreloConfirmationMessage(customerName)}
          </div>
          {error && (
            <div className="side-panel__alert side-panel__alert--error">{error}</div>
          )}
        </SidePanel>
      ) : (
        <SidePanel
          title="Add service"
          submitLabel={isSaving ? 'Adding…' : 'Add service'}
          isValid={!isSaving && !isLoading && !!selectedServiceId}
          onClose={() => {
            if (!isSaving) handleClose();
          }}
          onSubmit={handleSubmit}
        >
          {error && (
            <div className="side-panel__alert side-panel__alert--error">{error}</div>
          )}

          {isLoading ? (
            <div className="side-panel__alert" style={{ color: '#888', border: 'none' }}>
              Loading services…
            </div>
          ) : (
            <>
              <SmartSelect
                label="Service"
                placeholder={serviceOptions.length === 0 ? 'No services available' : 'Select…'}
                value={selectedServiceId || null}
                options={serviceOptions}
                onChange={(v) => {
                  setSelectedServiceId(v ?? '');
                  if (error) setError(null);
                }}
              />

              <SmartSelect
                label="Status"
                value={String(status)}
                options={statusOptions}
                onChange={(v) => {
                  if (v != null) handleStatusChange(Number(v));
                }}
              />

              <DateField
                label="Onboarding date"
                value={onboardingDate || null}
                onChange={(iso) => {
                  const newOnboardingDate = iso ?? '';
                  setOnboardingDate(newOnboardingDate);
                  if (!newOnboardingDate) return;
                  if (!startDateManuallySet.current || !startDate || startDate < newOnboardingDate) {
                    setStartDate(newOnboardingDate);
                  }
                }}
              />

              <DateField
                label="Start date"
                value={startDate || null}
                onChange={(iso) => {
                  setStartDate(iso ?? '');
                  startDateManuallySet.current = true;
                }}
                disabled={status === ONBOARDING_STATUS_VALUE}
              />

              <FormField label="Notes (optional)">
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormField>
            </>
          )}
        </SidePanel>
      )}
    </Dialog>
  );
};
